import { DeleteOutlined, HolderOutlined, KeyOutlined, PlusOutlined, SaveOutlined, UserAddOutlined } from '@ant-design/icons';
import { Badge, Button, Collapse, Input, InputNumber, Select, Tabs, Tag, Tooltip, message } from 'antd';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { LayoutSite } from '../components/layout';
import { get_settings_bdd, get_settings_tracks, save_settings_bdd, save_settings_tracks, get_settings_sources, save_settings_sources } from '../services/api.service';
import './settings.scss';

/* -------------------------------------------------------------------------------- *\
|                               Types
\* -------------------------------------------------------------------------------- */
interface TrackCode
{
    distance: number;
    type: string;
    code: string;
    track?: string;
    extra?: string;
}

interface TrackEntry
{
    track: string;
    code?: string;
    apicode?: string;
    codes: TrackCode[];
}

interface UserRow
{
    login: string;
    roles: string[];
    newPassword?: string;
    pendingPwd: string;
    editingPwd: boolean;
    isNew: boolean;
}

const KNOWN_ROLES = ['admin', 'client', 'viewer', 'imu', 'tracks', 'ICP', 'FRANCE', 'USA', 'QA', 'MST', 'GOLD', 'HKJC'];


/* -------------------------------------------------------------------------------- *\
|                               BddEditor
\* -------------------------------------------------------------------------------- */
const BddEditor: FC<{
    initialUsers: { login: string; roles: string[] }[];
    onSave: (users: { login: string; roles: string[]; newPassword?: string }[]) => Promise<void>;
    saving: boolean;
    disabled?: boolean;
}> = ({ initialUsers, onSave, saving, disabled }) =>
{
    const [users, setUsers] = useState<UserRow[]>(() =>
        initialUsers.map(u => ({ login: u.login, roles: u.roles, pendingPwd: '', editingPwd: false, isNew: false }))
    );
    const [availableRoles, setAvailableRoles] = useState<string[]>(() =>
    {
        const extra = initialUsers.flatMap(u => u.roles).filter(r => !KNOWN_ROLES.includes(r));
        return [...KNOWN_ROLES, ...extra.filter(r => !KNOWN_ROLES.includes(r))];
    });
    const [addingRole, setAddingRole] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');

    /* -------------------------------------------------------------------------------- *\
    |                               update helpers
    \* -------------------------------------------------------------------------------- */
    function updateUser(idx: number, patch: Partial<UserRow>)
    {
        setUsers(prev => prev.map((u, i) => i === idx ? { ...u, ...patch } : u));
    }

    function removeUser(idx: number)
    {
        setUsers(prev => prev.filter((_, i) => i !== idx));
    }

    function addUser()
    {
        setUsers(prev => [...prev, {
            login: '',
            roles: ['client'],
            pendingPwd: '',
            editingPwd: true,
            isNew: true,
            newPassword: undefined,
        }]);
    }

    function confirmPassword(idx: number)
    {
        const pwd = users[idx].pendingPwd.trim();
        if (!pwd) return;
        updateUser(idx, { newPassword: pwd, pendingPwd: '', editingPwd: false });
    }

    function cancelPassword(idx: number)
    {
        updateUser(idx, { pendingPwd: '', editingPwd: false });
    }

    function confirmNewRole()
    {
        const role = newRoleName.trim();
        if (!role) return;
        if (availableRoles.includes(role)) { message.warning(`Role "${role}" already exists`); return; }
        setAvailableRoles(prev => [...prev, role]);
        setNewRoleName('');
        setAddingRole(false);
    }

    function cancelNewRole()
    {
        setNewRoleName('');
        setAddingRole(false);
    }

    function handleSave()
    {
        for (const u of users)
        {
            if (!u.login.trim()) { message.error('A login is empty'); return; }
            if (u.isNew && !u.newPassword) { message.error(`Password required for "${u.login}"`); return; }
        }
        onSave(users.map(u => ({
            login: u.login.trim(),
            roles: u.roles,
            ...(u.newPassword ? { newPassword: u.newPassword } : {}),
        })));
    }

    return (
        <div className="settings__bdd">
            <div className="settings__bdd-toolbar">
                <Button icon={<UserAddOutlined />} onClick={addUser} disabled={disabled}>
                    Add user
                </Button>
                {addingRole
                    ? <div className="settings__bdd-newrole">
                        <Input
                            size="small"
                            value={newRoleName}
                            onChange={e => setNewRoleName(e.target.value)}
                            onPressEnter={confirmNewRole}
                            placeholder="role name"
                            autoFocus
                            className="settings__bdd-newrole-input"
                        />
                        <Button size="small" type="primary" onClick={confirmNewRole} disabled={!newRoleName.trim()}>✓</Button>
                        <Button size="small" onClick={cancelNewRole}>✕</Button>
                      </div>
                    : <Button icon={<PlusOutlined />} onClick={() => setAddingRole(true)} disabled={disabled}>
                        Create role
                      </Button>
                }
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} disabled={disabled}>
                    Save
                </Button>
            </div>

            <div className="settings__bdd-table">
                <div className="settings__bdd-thead">
                    <span className="settings__bdd-col settings__bdd-col--login">Login</span>
                    <span className="settings__bdd-col settings__bdd-col--roles">Roles</span>
                    <span className="settings__bdd-col settings__bdd-col--pwd">Password</span>
                    <span className="settings__bdd-col settings__bdd-col--del" />
                </div>

                {users.map((u, idx) => (
                    <div key={idx} className="settings__bdd-row">
                        {/* Login */}
                        <div className="settings__bdd-col settings__bdd-col--login">
                            {u.isNew
                                ? <Input
                                    size="small"
                                    value={u.login}
                                    onChange={e => updateUser(idx, { login: e.target.value })}
                                    placeholder="login"
                                    className="settings__bdd-input"
                                  />
                                : <span className="settings__bdd-login">{u.login}</span>
                            }
                        </div>

                        {/* Roles */}
                        <div className="settings__bdd-col settings__bdd-col--roles">
                            <Select
                                mode="multiple"
                                size="small"
                                value={u.roles}
                                onChange={v => updateUser(idx, { roles: v })}
                                className="settings__bdd-roles"
                                options={availableRoles.map(r => ({ value: r, label: r }))}
                                listHeight={400}
                                tagRender={({ value, closable, onClose }) => (
                                    <Tag
                                        closable={closable}
                                        onClose={onClose}
                                        style={{ marginRight: 2, fontSize: 11, color: '#1a1a1a', backgroundColor: '#e2bf67', borderColor: '#d4a843' }}
                                    >
                                        {value as string}
                                    </Tag>
                                )}
                            />
                        </div>

                        {/* Password */}
                        <div className="settings__bdd-col settings__bdd-col--pwd">
                            {u.editingPwd
                                ? <div className="settings__bdd-pwdinput">
                                    <Input.Password
                                        size="small"
                                        value={u.pendingPwd}
                                        onChange={e => updateUser(idx, { pendingPwd: e.target.value })}
                                        placeholder="new password"
                                        onPressEnter={() => confirmPassword(idx)}
                                        autoFocus
                                        className="settings__bdd-input"
                                    />
                                    <Button size="small" type="primary" onClick={() => confirmPassword(idx)} disabled={!u.pendingPwd.trim()}>✓</Button>
                                    {!u.isNew && <Button size="small" onClick={() => cancelPassword(idx)}>✕</Button>}
                                  </div>
                                : <Tooltip title={u.newPassword ? 'Password changed (not yet saved)' : 'Click to change'}>
                                    <Button
                                        size="small"
                                        icon={<KeyOutlined />}
                                        onClick={() => updateUser(idx, { editingPwd: true, pendingPwd: '' })}
                                        type={u.newPassword ? 'primary' : 'default'}
                                        className="settings__bdd-pwdbtn"
                                    >
                                        {u.newPassword ? 'Changed' : '••••••••'}
                                    </Button>
                                  </Tooltip>
                            }
                        </div>

                        {/* Delete */}
                        <div className="settings__bdd-col settings__bdd-col--del">
                            <Button
                                size="small"
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => removeUser(idx)}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------------- *\
|                               CodesTable
\* -------------------------------------------------------------------------------- */
const CodesTable: FC<{
    codes: TrackCode[];
    onChange: (codes: TrackCode[]) => void;
}> = ({ codes, onChange }) =>
{
    const dragIdx = useRef<number | null>(null);
    const dragFromHandle = useRef(false);
    const [dragOver, setDragOver] = useState<number | null>(null);

    function update(idx: number, field: keyof TrackCode, value: any)
    {
        onChange(codes.map((c, i) => i === idx ? { ...c, [field]: value } : c));
    }

    function remove(idx: number)
    {
        onChange(codes.filter((_, i) => i !== idx));
    }

    function handleDrop(toIdx: number)
    {
        const fromIdx = dragIdx.current;
        setDragOver(null);
        if (fromIdx === null || fromIdx === toIdx) return;
        const next = [...codes];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        onChange(next);
        dragIdx.current = null;
    }

    function add()
    {
        onChange([...codes, { distance: 0, type: 'PLAT', code: '' }]);
    }

    return (
        <div className="settings__codes">
            <div className="settings__codes-header">
                <span className="settings__codes-col settings__codes-col--order" />
                <span className="settings__codes-col settings__codes-col--code">Code</span>
                <span className="settings__codes-col settings__codes-col--type">Type</span>
                <span className="settings__codes-col settings__codes-col--dist">Distance</span>
                <span className="settings__codes-col settings__codes-col--surface">Surface</span>
                <span className="settings__codes-col settings__codes-col--extra">Extra</span>
                <span className="settings__codes-col settings__codes-col--del" />
            </div>
            {codes.map((c, idx) => (
                <div key={idx}
                    className={`settings__codes-row${dragOver === idx ? ' settings__codes-row--dragover' : ''}`}
                    draggable
                    onDragStart={e => { if (!dragFromHandle.current) { e.preventDefault(); return; } e.stopPropagation(); dragIdx.current = idx; }}
                    onDragEnd={e => { e.stopPropagation(); dragFromHandle.current = false; }}
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOver(idx); }}
                    onDragLeave={e => { e.stopPropagation(); setDragOver(null); }}
                    onDrop={e => { e.stopPropagation(); handleDrop(idx); }}
                >
                    <div className="settings__codes-col settings__codes-col--order">
                        <HolderOutlined className="settings__drag-handle" onMouseDown={() => { dragFromHandle.current = true; }} />
                    </div>
                    <Input
                        size="small"
                        value={c.code}
                        onChange={e => update(idx, 'code', e.target.value)}
                        className="settings__codes-col settings__codes-col--code"
                        placeholder="GA"
                    />
                    <Select
                        size="small"
                        value={c.type}
                        onChange={v => update(idx, 'type', v)}
                        className="settings__codes-col settings__codes-col--type"
                        options={[
                            { value: 'CROSS', label: 'CROSS' },
                            { value: 'HAIES', label: 'HAIES' },
                            { value: 'PLAT', label: 'PLAT' },
                            { value: 'STEEPLE', label: 'STEEPLE' },
                            { value: 'TROT', label: 'TROT' },
                        ]}
                    />
                    <InputNumber
                        size="small"
                        value={c.distance}
                        onChange={v => update(idx, 'distance', v ?? 0)}
                        className="settings__codes-col settings__codes-col--dist"
                        min={0}
                    />
                    <Input
                        size="small"
                        value={c.track ?? ''}
                        onChange={e => update(idx, 'track', e.target.value || undefined)}
                        className="settings__codes-col settings__codes-col--surface"
                        placeholder="TURF / AWT"
                    />
                    <Input
                        size="small"
                        value={c.extra ?? ''}
                        onChange={e => update(idx, 'extra', e.target.value || undefined)}
                        className="settings__codes-col settings__codes-col--extra"
                        placeholder="extra"
                    />
                    <Button
                        size="small"
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(idx)}
                        className="settings__codes-col settings__codes-col--del"
                    />
                </div>
            ))}
            <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={add}
                className="settings__add-code"
            >
                Add code
            </Button>
        </div>
    );
};

/* -------------------------------------------------------------------------------- *\
|                               TracksEditor
\* -------------------------------------------------------------------------------- */
const TracksEditor: FC<{
    data: TrackEntry[];
    onChange: (data: TrackEntry[]) => void;
    onSave: () => void;
    saving: boolean;
    disabled?: boolean;
}> = ({ data, onChange, onSave, saving, disabled }) =>
{
    const dragTrackRef = useRef<number | null>(null);
    const dragTrackFromHandle = useRef(false);
    const [dragTrackOver, setDragTrackOver] = useState<number | null>(null);
    const [openTracks, setOpenTracks] = useState<Record<number, boolean>>({});
    const [draggingTrack, setDraggingTrack] = useState(false);

    function updateName(idx: number, name: string)
    {
        onChange(data.map((t, i) => i === idx ? { ...t, track: name } : t));
    }

    function updateCode(idx: number, code: string)
    {
        onChange(data.map((t, i) => i === idx ? { ...t, code: code || undefined } : t));
    }

    function updateApicode(idx: number, apicode: string)
    {
        onChange(data.map((t, i) => i === idx ? { ...t, apicode: apicode || undefined } : t));
    }

    function updateCodes(idx: number, codes: TrackCode[])
    {
        onChange(data.map((t, i) => i === idx ? { ...t, codes } : t));
    }

    function removeTrack(idx: number)
    {
        onChange(data.filter((_, i) => i !== idx));
    }

    function addTrack()
    {
        onChange([...data, { track: '', codes: [] }]);
    }

    function handleTrackDrop(toIdx: number)
    {
        const fromIdx = dragTrackRef.current;
        setDragTrackOver(null);
        if (fromIdx === null || fromIdx === toIdx) return;
        const next = [...data];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        onChange(next);
        dragTrackRef.current = null;
    }

    return (
        <div className="settings__tracks">
            <div className="settings__tracks-toolbar">
                <Button icon={<PlusOutlined />} onClick={addTrack} disabled={disabled}>
                    Add track
                </Button>
                <Button type="primary" icon={<SaveOutlined />} onClick={onSave} loading={saving} disabled={disabled}>
                    Save
                </Button>
            </div>
            {data.map((track, trackIdx) => (
                <div
                    key={trackIdx}
                    draggable
                    onDragStart={e => { if (!dragTrackFromHandle.current) { e.preventDefault(); return; } dragTrackRef.current = trackIdx; setDraggingTrack(true); }}
                    onDragEnd={() => { dragTrackFromHandle.current = false; setDraggingTrack(false); setOpenTracks({}); }}
                    onDragOver={e => { e.preventDefault(); setDragTrackOver(trackIdx); }}
                    onDragLeave={() => setDragTrackOver(null)}
                    onDrop={() => handleTrackDrop(trackIdx)}
                    className={`settings__track-wrap${dragTrackOver === trackIdx ? ' settings__track-wrap--dragover' : ''}`}
                >
                    <Collapse
                        className="settings__collapse"
                        activeKey={draggingTrack ? [] : (openTracks[trackIdx] ? ['0'] : [])}
                        onChange={keys => setOpenTracks(m => ({ ...m, [trackIdx]: (keys as string[]).length > 0 }))}
                        items={[{
                            key: '0',
                            collapsible: 'icon' as const,
                            label: (
                                <div className="settings__track-header">
                                    <HolderOutlined className="settings__drag-handle" onClick={e => e.stopPropagation()} onMouseDown={() => { dragTrackFromHandle.current = true; }} />
                                    <Input
                                        value={track.code ?? ''}
                                        onChange={e => updateCode(trackIdx, e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        className="settings__track-code"
                                        placeholder="Code"
                                    />
                                    <Input
                                        value={track.track}
                                        onChange={e => updateName(trackIdx, e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        className="settings__track-name"
                                        placeholder="Track name"
                                    />
                                    <Input
                                        value={track.apicode ?? ''}
                                        onChange={e => updateApicode(trackIdx, e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        className="settings__track-code"
                                        placeholder="API"
                                        maxLength={5}
                                    />
                                    <Badge count={track.codes.length} color="#888" className="settings__track-badge" />
                                    <Button
                                        danger
                                        size="small"
                                        type="text"
                                        icon={<DeleteOutlined />}
                                        onClick={e => { e.stopPropagation(); removeTrack(trackIdx); }}
                                    />
                                </div>
                            ),
                            children: (
                                <CodesTable
                                    codes={track.codes}
                                    onChange={codes => updateCodes(trackIdx, codes)}
                                />
                            ),
                        }]}
                    />
                </div>
            ))}
        </div>
    );
};

/* -------------------------------------------------------------------------------- *\
|                               SourcesEditor
\* -------------------------------------------------------------------------------- */
const SourcesEditor: FC<{
    sources: string[];
    onChange: (sources: string[]) => void;
    onSave: () => void;
    saving: boolean;
}> = ({ sources, onChange, onSave, saving }) =>
{
    const [editingIdx, setEditingIdx] = useState<number | null>(null);
    const [editingVal, setEditingVal] = useState('');

    function startEdit(idx: number)
    {
        setEditingIdx(idx);
        setEditingVal(sources[idx]);
    }

    function commitEdit()
    {
        if (editingIdx === null) return;
        const val = editingVal.trim();
        if (!val) { setEditingIdx(null); return; }
        onChange(sources.map((s, i) => i === editingIdx ? val : s));
        setEditingIdx(null);
    }

    function remove(idx: number)
    {
        onChange(sources.filter((_, i) => i !== idx));
    }

    function add()
    {
        onChange([...sources, '']);
        setEditingIdx(sources.length);
        setEditingVal('');
    }

    return (
        <div className="settings__sources">
            <div className="settings__sources-toolbar">
                <Button icon={<PlusOutlined />} onClick={add}>Add source</Button>
                <Button type="primary" icon={<SaveOutlined />} onClick={onSave} loading={saving}>Save</Button>
            </div>
            <div className="settings__sources-list">
                {sources.map((s, idx) => (
                    <div key={idx} className="settings__sources-row">
                        {editingIdx === idx
                            ? <>
                                <Input
                                    size="small"
                                    value={editingVal}
                                    onChange={e => setEditingVal(e.target.value)}
                                    onPressEnter={commitEdit}
                                    autoFocus
                                    className="settings__sources-input"
                                />
                                <Button size="small" type="primary" onClick={commitEdit} disabled={!editingVal.trim()}>✓</Button>
                                <Button size="small" onClick={() => setEditingIdx(null)}>✕</Button>
                              </>
                            : <>
                                <span className="settings__sources-name" onClick={() => startEdit(idx)}>{s}</span>
                                <Button size="small" type="text" onClick={() => startEdit(idx)}>Edit</Button>
                                <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={() => remove(idx)} />
                              </>
                        }
                    </div>
                ))}
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------------- *\
|                               SettingsPage
\* -------------------------------------------------------------------------------- */
export const SettingsPage: FC<{ children?: ReactNode }> = () =>
{
    const [savingBdd, setSavingBdd] = useState(false);
    const [bddUsers, setBddUsers] = useState<{ login: string; roles: string[] }[] | null>(null);
    const [bddError, setBddError] = useState(false);
    const [tracksData, setTracksData] = useState<TrackEntry[]>([]);
    const [tracksError, setTracksError] = useState(false);
    const [savingTracks, setSavingTracks] = useState(false);
    const [activeSources, setActiveSources] = useState<string[]>([]);
    const [savingSources, setSavingSources] = useState(false);

    useEffect(() =>
    {
        get_settings_bdd().then(r =>
        {
            if (r.status) setBddUsers(r.users);
            else { setBddUsers([]); setBddError(true); }
        }).catch(() => { message.error('Failed to load users'); setBddUsers([]); setBddError(true); });

        get_settings_tracks().then(r =>
        {
            if (r.status)
            {
                try { setTracksData(JSON.parse(r.content) as TrackEntry[]); }
                catch { message.error('Invalid tracks data'); setTracksError(true); }
            }
            else { setTracksError(true); }
        }).catch(() => { message.error('Failed to load tracks'); setTracksError(true); });

        get_settings_sources().then(r =>
        {
            if (r.status) setActiveSources(r.sources);
            else message.error('Failed to load sources');
        }).catch(() => message.error('Failed to load sources'));
    }, []);

    /* -------------------------------------------------------------------------------- *\
    |                               handleSaveBdd
    \* -------------------------------------------------------------------------------- */
    async function handleSaveBdd(users: { login: string; roles: string[]; newPassword?: string }[])
    {
        setSavingBdd(true);
        try
        {
            const r = await save_settings_bdd(users);
            if (r.status) message.success('Users saved');
            else message.error(r.message);
        }
        catch { message.error('Save error'); }
        finally { setSavingBdd(false); }
    }

    /* -------------------------------------------------------------------------------- *\
    |                               handleSaveTracks
    \* -------------------------------------------------------------------------------- */
    async function handleSaveTracks()
    {
        setSavingTracks(true);
        try
        {
            const content = JSON.stringify(tracksData, null, '\t');
            const r = await save_settings_tracks(content);
            if (r.status) message.success('Tracks saved');
            else message.error(r.message);
        }
        catch { message.error('Save error'); }
        finally { setSavingTracks(false); }
    }

    /* -------------------------------------------------------------------------------- *\
    |                               handleSaveSources
    \* -------------------------------------------------------------------------------- */
    async function handleSaveSources()
    {
        setSavingSources(true);
        try
        {
            const r = await save_settings_sources(activeSources);
            if (r.status) message.success('Sources saved');
            else message.error(r.message);
        }
        catch { message.error('Save error'); }
        finally { setSavingSources(false); }
    }

    const tabs = [
        {
            key: 'bdd',
            label: 'Users',
            children: bddUsers === null
                ? <div className="settings__loading">Loading…</div>
                : <BddEditor
                    initialUsers={bddUsers}
                    onSave={handleSaveBdd}
                    saving={savingBdd}
                    disabled={bddError}
                  />,
        },
        {
            key: 'tracks',
            label: 'Tracks',
            children: (
                <TracksEditor
                    data={tracksData}
                    onChange={setTracksData}
                    onSave={handleSaveTracks}
                    saving={savingTracks}
                    disabled={tracksError}
                />
            ),
        },
        {
            key: 'sources',
            label: 'Sources',
            children: (
                <SourcesEditor
                    sources={activeSources}
                    onChange={setActiveSources}
                    onSave={handleSaveSources}
                    saving={savingSources}
                />
            ),
        },
    ];

    return (
        <LayoutSite>
            <div className="settings">
                <h2 className="settings__title">Settings</h2>
                <Tabs items={tabs} className="settings__tabs" />
            </div>
        </LayoutSite>
    );
};
